import React, { useCallback, useState, useRef } from 'react';
import {
  Webhook,
  Clock,
  Play,
  Globe,
  Code,
  Sparkles,
  MessageSquare,
  MessageCircle,
  GitBranch,
  Network,
  Timer,
  X,
  MousePointerClick,
  Keyboard,
  FileSearch,
  Camera,
  Upload,
  Hourglass,
  ArrowDownUp,
  ListFilter,
  Terminal,
  ImagePlus,
  Paintbrush,
  Tags,
  Shuffle,
  Database,
  Merge,
  Filter,
  Bot,
  ClipboardPlus,
  Repeat,
  RotateCcw,
  ShieldAlert,
} from 'lucide-react';
import { WorkflowNode, getNodeDefinition, Position } from '../helpers/workflowTypes';
import styles from './WorkflowNode.module.css';

// Icon mapping
const ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Webhook,
  Clock,
  Play,
  Globe,
  Code,
  Sparkles,
  MessageSquare,
  MessageCircle,
  GitBranch,
  Network,
  Timer,
  MousePointerClick,
  Keyboard,
  FileSearch,
  Camera,
  Upload,
  Hourglass,
  ArrowDownUp,
  ListFilter,
  Terminal,
  ImagePlus,
  Paintbrush,
  Tags,
  Shuffle,
  Database,
  Merge,
  Filter,
  Bot,
  ClipboardPlus,
  Repeat,
  RotateCcw,
  ShieldAlert,
};

interface WorkflowNodeComponentProps {
  node: WorkflowNode;
  isSelected: boolean;
  onSelect: (nodeId: string, e?: React.MouseEvent) => void;
  onMove: (nodeId: string, position: Position) => void;
  onMoveEnd: () => void;
  onDelete: (nodeId: string) => void;
  onConnectionStart: (nodeId: string, handle: string) => void;
  onConnectionEnd: (nodeId: string, handle: string) => void;
  isConnecting: boolean;
  zoom: number;
}

export const WorkflowNodeComponent: React.FC<WorkflowNodeComponentProps> = ({
  node,
  isSelected,
  onSelect,
  onMove,
  onMoveEnd,
  onDelete,
  onConnectionStart,
  onConnectionEnd,
  isConnecting,
  zoom,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  const nodeRef = useRef<HTMLDivElement>(null);

  const definition = getNodeDefinition(node.type);
  const Icon = definition?.icon ? ICONS[definition.icon] : null;
  const configSummaryItems = (definition?.configFields || [])
    .map((field) => {
      const rawValue = node.config?.[field.key as keyof typeof node.config];
      if (
        rawValue === undefined ||
        rawValue === null ||
        rawValue === '' ||
        (Array.isArray(rawValue) && rawValue.length === 0)
      ) {
        return field.required ? `${field.label}: missing` : null;
      }

      if (typeof rawValue === 'object') {
        return `${field.label}: configured`;
      }

      const normalizedValue = String(rawValue).trim();
      if (!normalizedValue) {
        return field.required ? `${field.label}: missing` : null;
      }

      const compactValue =
        normalizedValue.length > 28 ? `${normalizedValue.slice(0, 28).trim()}...` : normalizedValue;
      return `${field.label}: ${compactValue}`;
    })
    .filter((value): value is string => Boolean(value))
    .slice(0, 2);

  // Get category color
  const getCategoryColor = () => {
    switch (definition?.category) {
      case 'trigger':
        return '#10b981';
      case 'browser':
        return '#8b5cf6';
      case 'ai':
        return '#ec4899';
      case 'data':
        return '#06b6d4';
      case 'action':
        return '#3b82f6';
      case 'taskingbot':
        return '#f97316';
      case 'logic':
        return '#f59e0b';
      default:
        return 'var(--color-text-secondary)';
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left click
    e.stopPropagation();

    onSelect(node.id, e);
    setIsDragging(true);

    const rect = nodeRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: (e.clientX - rect.left) / zoom,
        y: (e.clientY - rect.top) / zoom,
      });
    }
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !nodeRef.current?.parentElement) return;

      const parent = nodeRef.current.parentElement;
      const parentRect = parent.getBoundingClientRect();

      const newX = (e.clientX - parentRect.left) / zoom - dragOffset.x;
      const newY = (e.clientY - parentRect.top) / zoom - dragOffset.y;

      onMove(node.id, { x: Math.max(0, newX), y: Math.max(0, newY) });
    },
    [isDragging, dragOffset, node.id, onMove, zoom],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onMoveEnd();
    }
  }, [isDragging, onMoveEnd]);

  // Add/remove global mouse listeners
  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle output click (start connection)
  const handleOutputClick = (e: React.MouseEvent, handle: string) => {
    e.stopPropagation();
    onConnectionStart(node.id, handle);
  };

  // Handle input click (end connection)
  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isConnecting) {
      onConnectionEnd(node.id, 'input');
    }
  };

  return (
    <div
      ref={nodeRef}
      className={`${styles.node} ${isSelected ? styles.selected : ''} ${isDragging ? styles.dragging : ''}`}
      style={{
        left: node.position.x,
        top: node.position.y,
        borderColor: isSelected ? getCategoryColor() : undefined,
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <div className={styles.header} style={{ backgroundColor: getCategoryColor() }}>
        {Icon && <Icon size={14} className={styles.icon} />}
        <span className={styles.name}>{node.name}</span>
        <button
          className={styles.deleteBtn}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(node.id);
          }}
        >
          <X size={12} />
        </button>
      </div>

      {/* Body */}
      <div className={styles.body}>
        <span className={styles.type}>{definition?.description}</span>
        {configSummaryItems.length > 0 && (
          <div className={styles.summary}>
            {configSummaryItems.map((item) => (
              <span key={item} className={styles.summaryItem}>
                {item}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Input handle */}
      {(definition?.inputs || 0) > 0 && (
        <div
          className={`${styles.handle} ${styles.inputHandle} ${isConnecting ? styles.connectable : ''}`}
          onClick={handleInputClick}
          title="Input"
        />
      )}

      {/* Output handles */}
      {Array.from({ length: definition?.outputs || 0 }).map((_, i) => {
        const label = definition?.outputLabels?.[i] || 'output';
        return (
          <div
            key={i}
            className={`${styles.handle} ${styles.outputHandle}`}
            style={{ top: `${40 + i * 24}px` }}
            onClick={(e) => handleOutputClick(e, label)}
            title={label}
          >
            {definition?.outputLabels && <span className={styles.handleLabel}>{label}</span>}
          </div>
        );
      })}
    </div>
  );
};
